import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Ban, ChevronDown, Download } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Modal } from "@/components/ui/Modal";
import { Spinner } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import {
  useDocAnonymizationModels,
  useDocAnonymizationMutations,
  useDocAnonymizations,
  useDocTemplateMutations,
  useDocTemplates,
  useDocTemplateVersions,
  useDocVariables,
} from "../hooks";
import { apiErr, fmtDate, fmtDateTime } from "../utils";
import type {
  DocAnonymization,
  DocAnonymizationStatus,
  DocTemplate,
  DocTemplateKind,
} from "../types";
import { DOC_TEMPLATE_KIND_LABELS } from "../types";
import { ManagedFileDownload, PageHeader, Tabs, TableShell, Th } from "../components/ui";
import { FormatkaTemplatesSection } from "../components/formatki/FormatkaTemplatesSection";

function errorMessage(err: unknown): string {
  const detail = (err as { response?: { data?: { errors?: { detail?: unknown } } } })?.response
    ?.data?.errors?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") {
    const first = Object.values(detail as Record<string, unknown>).flat()[0];
    if (typeof first === "string") return first;
  }
  return "Nie udało się zapisać szablonu.";
}

function FragmentGroup({ group }: { group: import("../types").DocVariableGroup }) {
  return (
    <>
      <tr className="bg-[var(--color-bg-base)]">
        <td colSpan={2} className="px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
          {group.label}
        </td>
      </tr>
      {group.variables.map((v) => (
        <tr key={v.name} className="border-t border-[var(--color-border-muted)] align-top">
          <td className="px-2 py-1 whitespace-nowrap">
            <span className="font-mono border border-[var(--color-border)] px-1 py-0.5 bg-[var(--color-bg-base)]">#{v.name}#</span>
          </td>
          <td className="px-2 py-1 text-[var(--color-text-secondary)]">{v.source ?? v.description}</td>
        </tr>
      ))}
    </>
  );
}

function TemplateFormModal({ template, onClose }: { template: DocTemplate | null; onClose: () => void }) {
  const m = useDocTemplateMutations();
  const [name, setName] = useState(template?.name ?? "");
  const [kind, setKind] = useState<DocTemplateKind>(template?.kind ?? "general");
  const [operator, setOperator] = useState(template?.operator ?? "energa");
  const [description, setDescription] = useState(template?.description ?? "");
  const [file, setFile] = useState<File | null>(null);
  const mutation = template ? m.update : m.create;

  // Variables accepted for the picked kind + where each is sourced from. A "general"
  // template sees only the groups shared by every context; a context-specific kind sees
  // that context's groups (backend tags each group with its `contexts`). Every app_*
  // kind shares the single `official_application` generation context (wnioski engine).
  // „Bez typu" nie ma własnego kontekstu (szablon nie jest nigdzie oferowany), więc
  // pokazujemy mu — jak uniwersalnemu — tylko grupy wspólne dla wszystkich kontekstów.
  const { data: varGroups } = useDocVariables(true);
  // Wzór operatora (.docx) wypełniany po układzie tabel — bez #zmiennych#, więc bez
  // listy zmiennych; backend sprawdza przy zapisie, czy układ zgadza się z generatorem.
  const docxForm = kind === "ze_report_form_energa";
  const kindContext: string | null =
    kind === "general" || kind === "none"
      ? null
      : kind.startsWith("app_")
        ? "official_application"
        : kind;
  const shownGroups = (varGroups ?? []).filter((g) => {
    const ctx = g.contexts ?? [];
    return kindContext
      ? ctx.includes(kindContext)
      : ctx.includes("private_consent") && ctx.includes("regulatory_application");
  });

  const submit = () => {
    if (!name.trim() || (!template && !file)) return;
    // operator (OSD) dotyczy każdego rodzaju: energa/tauron = wariant „niewspólny",
    // inny = wspólny/uniwersalny (oferowany na projektach obu operatorów).
    const payload = {
      name: name.trim(), kind, operator,
      description: description.trim(), file: file ?? undefined,
    };
    if (template) {
      m.update.mutate({ id: template.id, ...payload }, { onSuccess: onClose });
    } else {
      m.create.mutate({ ...payload, file: file! }, { onSuccess: onClose });
    }
  };

  return (
    <Modal open onClose={onClose} title={template ? "Edytuj szablon" : "Nowy szablon"}>
      <div className="space-y-3">
        <label className="block">
          <span className="section-label">Nazwa szablonu *</span>
          <input className="input-base mt-1" value={name} autoFocus onChange={(e) => setName(e.target.value)} />
        </label>

        <label className="block">
          <span className="section-label">Rodzaj szablonu</span>
          <select
            className="input-base mt-1"
            value={kind}
            onChange={(e) => setKind(e.target.value as DocTemplateKind)}
          >
            {(Object.keys(DOC_TEMPLATE_KIND_LABELS) as DocTemplateKind[]).map((k) => (
              <option key={k} value={k}>{DOC_TEMPLATE_KIND_LABELS[k]}</option>
            ))}
          </select>
          <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
            Decyduje, w którym oknie generowania szablon się pojawi. „Uniwersalny" widoczny wszędzie,
            „Bez typu" — nigdzie (szablon zostaje w rejestrze, ale nie jest do wyboru).
          </p>
        </label>

        {/* Operator (OSD) — dla każdego rodzaju: okna generowania dobierają szablon
            pod operatora klienta projektu; „Inny / uniwersalny" = wspólny dla obu. */}
        <label className="block">
          <span className="section-label">Operator (OSD)</span>
          <select className="input-base mt-1" value={operator} onChange={(e) => setOperator(e.target.value)}>
            <option value="energa">ENERGA</option>
            <option value="tauron">TAURON</option>
            <option value="inny">Inny / uniwersalny (wspólny)</option>
          </select>
          <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
            Okna generowania (WPW, zgody, regulacje, wnioski) proponują szablony pod operatora
            klienta projektu (ENERGA/TAURON). „Uniwersalny" jest oferowany na projektach obu.
          </p>
        </label>

        {/* Akceptowane zmienne dla wybranego rodzaju + skąd są liczone. */}
        <div>
          <span className="section-label">Akceptowane zmienne — skąd liczone</span>
          <div className="mt-1 max-h-56 overflow-y-auto border border-[var(--color-border-muted)]">
            <table className="w-full text-[11px]">
              <thead className="sticky top-0 bg-[var(--color-bg-elevated)]">
                <tr className="text-left">
                  <th className="px-2 py-1 font-mono uppercase tracking-wide text-[10px] text-[var(--color-text-muted)]">Zmienna</th>
                  <th className="px-2 py-1 font-mono uppercase tracking-wide text-[10px] text-[var(--color-text-muted)]">Skąd liczona</th>
                </tr>
              </thead>
              <tbody>
                {shownGroups.map((g) => (
                  <FragmentGroup key={g.key} group={g} />
                ))}
                {shownGroups.length === 0 && (
                  <tr>
                    <td colSpan={2} className="px-2 py-3 text-center section-label">Ładowanie zmiennych…</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
            Wstaw w dokumencie jako <span className="font-mono">#nazwa#</span>. Braki danych per-projekt
            uzupełnij w zakładce Metadane (kolumny → <span className="font-mono">#projekt_meta_…#</span>).
          </p>
        </div>

        <label className="block">
          <span className="section-label">Opis</span>
          <textarea
            className="input-base mt-1 min-h-[60px]"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>

        <div>
          <span className="section-label">
            {template
              ? `Wgraj nową wersję pliku ${docxForm ? "Word (.docx)" : "ODF"} (opcjonalnie — obecnie v${template.current_version})`
              : docxForm
                ? "Plik wzoru operatora (.docx) *"
                : "Plik szablonu ODF (.odt) *"}
          </span>
          <FileDropZone
            className="mt-1"
            accept={docxForm ? ".docx" : ".odt,.ott"}
            files={file ? [file] : []}
            onFilesChange={(fs) => setFile(fs[0] ?? null)}
          />
          <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
            {docxForm ? (
              <>
                Oryginalny wzór ENERGA „Załącznik nr 4 do OWU” — generator raportu okresowego
                wypełnia jego rubryki po układzie tabel (metryka 8 rubryk, TABELA 1 z 5
                kolumnami, TABELA NR 2 z 3). Plik o innym układzie zostanie odrzucony.
              </>
            ) : (
              <>
                Miejsca do automatycznego wypełnienia oznacz w dokumencie jako{" "}
                <span className="font-mono">#nazwa_zmiennej#</span> — zostaną wykryte przy zapisie.
              </>
            )}
            {template && " Poprzednia wersja zostaje w historii i nadal da się ją pobrać."}
          </p>
        </div>

        {mutation.isError && (
          <p className="text-xs text-[var(--color-primary)] font-mono">{errorMessage(mutation.error)}</p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={onClose}>Anuluj</Button>
          <Button
            size="sm"
            disabled={!name.trim() || (!template && !file)}
            loading={mutation.isPending}
            onClick={submit}
          >
            {template ? "Zapisz" : "Utwórz"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function TemplateHistoryModal({ template, onClose }: { template: DocTemplate; onClose: () => void }) {
  const { data, isLoading } = useDocTemplateVersions(template.id);
  const rows = data ?? [];

  return (
    <Modal open size="lg" onClose={onClose} title={`Historia wersji — ${template.name}`}>
      {isLoading ? (
        <div className="py-8 flex justify-center"><Spinner /></div>
      ) : (
        <div className="max-h-96 overflow-y-auto border border-[var(--color-border-muted)]">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[var(--color-bg-elevated)]">
              <tr className="text-left">
                <th className="px-2 py-1.5 font-mono uppercase tracking-wide text-[10px] text-[var(--color-text-muted)]">Wersja</th>
                <th className="px-2 py-1.5 font-mono uppercase tracking-wide text-[10px] text-[var(--color-text-muted)]">Wgrał(a)</th>
                <th className="px-2 py-1.5 font-mono uppercase tracking-wide text-[10px] text-[var(--color-text-muted)]">Kiedy</th>
                <th className="px-2 py-1.5 font-mono uppercase tracking-wide text-[10px] text-[var(--color-text-muted)] text-right">Plik</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((v) => (
                <tr key={v.id} className="border-t border-[var(--color-border-muted)] align-top">
                  <td className="px-2 py-1.5 whitespace-nowrap font-mono">
                    v{v.version}
                    {v.version === template.current_version && (
                      <span className="ml-1.5 text-[10px] uppercase tracking-wide px-1 py-0.5 bg-[var(--color-bg-elevated)] border border-[var(--color-border)] text-[var(--color-text-secondary)]">
                        obecna
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1.5">{v.uploaded_by_name ?? "—"}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap font-mono text-[11px]">{fmtDateTime(v.created_at)}</td>
                  <td className="px-2 py-1.5 text-right">
                    {v.file?.url ? (
                      <ManagedFileDownload
                        file={v.file}
                        className="inline-flex items-center gap-1 text-[11px] font-mono text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                        title={`Pobierz ${v.file.original_filename}`}
                      >
                        <Download size={13} /> {v.file.original_filename}
                      </ManagedFileDownload>
                    ) : (
                      <span className="section-label">brak pliku</span>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-2 py-6 text-center section-label tracking-wide">
                    Brak historii wgrań.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex justify-end pt-3">
        <Button variant="ghost" size="sm" onClick={onClose}>Zamknij</Button>
      </div>
    </Modal>
  );
}

function TemplatesTab() {
  const user = useAuthStore((s) => s.user);
  const { data, isLoading } = useDocTemplates();
  const m = useDocTemplateMutations();
  const [onlyMine, setOnlyMine] = useState(true);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<DocTemplate | null>(null);
  const [creating, setCreating] = useState(false);
  const [historyOf, setHistoryOf] = useState<DocTemplate | null>(null);

  const canManage = (t: DocTemplate) =>
    !!user &&
    (t.author.id === user.id || user.role === "director" || user.is_ceo || user.is_developer);

  const rows = useMemo(() => {
    let list = data ?? [];
    if (onlyMine && user) list = list.filter((t) => t.author.id === user.id);
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((t) => t.name.toLowerCase().includes(q));
    return list;
  }, [data, onlyMine, query, user]);

  return (
    <>
      <div className="mb-4">
        <VariablesInfoPanel />
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <input
          className="input-base max-w-xs text-sm py-1.5"
          placeholder="Filtruj po nazwie…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <label className="flex items-center gap-1.5 text-xs font-mono cursor-pointer select-none">
          <input type="checkbox" checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} />
          Tylko moje
        </label>
        <div className="ml-auto">
          <Button size="sm" onClick={() => setCreating(true)}>+ Nowy szablon</Button>
        </div>
      </div>

      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : (
        <TableShell>
          <thead>
            <tr>
              <Th>Nazwa</Th>
              <Th>Zmienne</Th>
              <Th>Autor</Th>
              <Th>Wersja</Th>
              <Th>Utworzono</Th>
              <Th className="text-right">Akcje</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.id} className="border-t border-[var(--color-border-muted)] hover:bg-[var(--color-bg-elevated)] transition-colors">
                <td className="px-3 py-2.5">
                  <div className="font-medium text-[var(--color-text-primary)] flex flex-wrap items-center gap-1.5">
                    {t.name}
                    {t.kind !== "general" && (
                      // „Bez typu" na bursztynowo — to nie kategoria, tylko sygnał, że
                      // szablon nie jest oferowany w żadnym oknie generowania.
                      <span
                        className={`text-[10px] font-mono uppercase tracking-wide px-1 py-0.5 border ${
                          t.kind === "none"
                            ? "border-amber-500 text-amber-600 dark:text-amber-400"
                            : "bg-[var(--color-bg-elevated)] border-[var(--color-border)] text-[var(--color-text-secondary)]"
                        }`}
                      >
                        {DOC_TEMPLATE_KIND_LABELS[t.kind]}
                      </span>
                    )}
                  </div>
                  {t.description && (
                    <div className="text-[11px] text-[var(--color-text-muted)]">{t.description}</div>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex flex-wrap gap-1">
                    {t.variables.map((v) => (
                      <span key={v} className="text-[11px] font-mono border border-[var(--color-border)] px-1.5 py-0.5 bg-[var(--color-bg-base)]">
                        #{v}#
                      </span>
                    ))}
                    {t.variables.length === 0 && <span className="section-label">brak</span>}
                  </div>
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap">{t.author.name}</td>
                <td className="px-3 py-2.5 whitespace-nowrap">
                  {/* Klik = historia wgrań: kto i kiedy wgrał którą wersję pliku. */}
                  <button
                    className="font-mono text-[11px] border border-[var(--color-border)] px-1.5 py-0.5 bg-[var(--color-bg-base)] hover:text-[var(--color-primary)] hover:border-[var(--color-primary)] transition-colors"
                    title="Pokaż historię wersji"
                    onClick={() => setHistoryOf(t)}
                  >
                    v{t.current_version}
                  </button>
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap font-mono text-[11px]">{fmtDate(t.created_at)}</td>
                <td className="px-3 py-2.5">
                  <div className="flex justify-end items-center gap-1.5">
                    {t.file?.url && (
                      <ManagedFileDownload
                        file={t.file}
                        className="inline-flex items-center gap-1 text-[11px] font-mono text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                        title={`Pobierz ${t.file.original_filename}`}
                      >
                        <Download size={13} /> Pobierz
                      </ManagedFileDownload>
                    )}
                    {canManage(t) && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => setEditing(t)}>Edytuj</Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => window.confirm(`Usunąć szablon „${t.name}"?`) && m.remove.mutate(t.id)}
                        >
                          Usuń
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center section-label tracking-wide">
                  {(data ?? []).length === 0
                    ? "Brak szablonów. Dodaj pierwszy szablon ODF ze zmiennymi #nazwa_zmiennej#."
                    : "Brak szablonów spełniających kryteria filtrowania."}
                </td>
              </tr>
            )}
          </tbody>
        </TableShell>
      )}

      {(creating || editing) && (
        <TemplateFormModal template={editing} onClose={() => { setCreating(false); setEditing(null); }} />
      )}
      {historyOf && (
        <TemplateHistoryModal template={historyOf} onClose={() => setHistoryOf(null)} />
      )}
    </>
  );
}

function VariablesInfoPanel() {
  const [open, setOpen] = useState(false);
  // Fetch lazily — the registry is only loaded once the panel is expanded.
  const { data: groups, isLoading } = useDocVariables(open);

  return (
    <div className="card overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[var(--color-bg-elevated)] transition-colors"
      >
        <span className="font-mono text-xs font-bold uppercase tracking-wider text-[var(--color-text-primary)]">
          Dostępne zmienne w szablonach
        </span>
        <ChevronDown
          size={16}
          className={`text-[var(--color-text-muted)] transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open &&
        (isLoading ? (
          <div className="py-8 flex justify-center"><Spinner /></div>
        ) : (
          (groups ?? []).map((g) => (
            <div key={g.key} className="border-t border-[var(--color-border-muted)] px-4 py-3">
              <h3 className="font-mono text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                {g.label}
              </h3>
              <p className="mt-0.5 text-[11px] text-[var(--color-text-muted)]">{g.description}</p>
              <table className="mt-2 w-full text-xs">
                <tbody>
                  {g.variables.map((v) => (
                    <tr key={v.name} className="border-t border-[var(--color-border-muted)]">
                      <td className="py-1.5 pr-3 whitespace-nowrap align-top w-px">
                        <span className="text-[11px] font-mono border border-[var(--color-border)] px-1.5 py-0.5 bg-[var(--color-bg-base)]">
                          #{v.name}#
                        </span>
                      </td>
                      <td className="py-1.5 text-[var(--color-text-secondary)]">
                        {v.description}
                        {v.source && (
                          <span className="block text-[10px] text-[var(--color-text-muted)]">
                            Skąd: {v.source}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {g.note && (
                <p className="mt-2 text-[11px] text-[var(--color-text-muted)] italic">{g.note}</p>
              )}
            </div>
          ))
        ))}
    </div>
  );
}

// ── Anonimizacja ───────────────────────────────────────────────────────────────

const ANON_STATUS: Record<DocAnonymizationStatus, { color: "gray" | "blue" | "green" | "red"; label: string }> = {
  pending: { color: "gray", label: "W kolejce" },
  running: { color: "blue", label: "W trakcie" },
  success: { color: "green", label: "Gotowe" },
  failure: { color: "red", label: "Błąd" },
  // Przerwane ręcznie — szary, nie czerwony: to decyzja użytkownika, nie awaria.
  cancelled: { color: "gray", label: "Anulowano" },
};

/** „12,4 MB" / „812 kB" — rozmiar pliku dla człowieka. */
function fmtBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1).replace(".", ",") + " MB";
  return Math.round(bytes / 1024) + " kB";
}

/** Koszt AI całego przebiegu. Pojedyncza anonimizacja bywa warta grosze, więc
 *  poniżej dolara pokazujemy cztery miejsca — dwa robiłyby z niej „$0,00". */
function fmtAnonCost(usd: string | null | undefined): string {
  const n = Number(usd ?? 0);
  if (!n) return "—";
  return "$" + n.toFixed(n >= 1 ? 2 : 4).replace(".", ",");
}

/** Ile trwał przebieg: „48 s", „4 min 12 s", „1 h 07 min". Dla przebiegu
 *  W TRAKCIE liczone do teraz — lista odświeża się co 3 s, więc licznik idzie. */
function fmtRunTime(from: string | null, to: string | null): string {
  if (!from) return "—";
  const total = Math.round((new Date(to ?? Date.now()).getTime() - new Date(from).getTime()) / 1000);
  if (!Number.isFinite(total) || total < 0) return "—";
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h) return `${h} h ${String(m).padStart(2, "0")} min`;
  if (m) return `${m} min ${String(s).padStart(2, "0")} s`;
  return `${s} s`;
}

/** Ta sama składnia co w oknie drukowania: „1-3, 7" / „2;5". Waliduje tylko
 *  kształt — zakres poza dokumentem odrzuca backend, który zna liczbę stron. */
function pageSpecValid(spec: string): boolean {
  const parts = spec.replace(/;/g, ",").split(",").map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return false;
  return parts.every((p) => /^\d+$/.test(p) || /^\d+\s*-\s*\d+$/.test(p));
}

function AnonymizationForm() {
  const { data: modelsData } = useDocAnonymizationModels();
  const m = useDocAnonymizationMutations();
  const [file, setFile] = useState<File | null>(null);
  const [allPages, setAllPages] = useState(true);
  const [spec, setSpec] = useState("");
  const [model, setModel] = useState("");
  const [fast, setFast] = useState(false);
  // „Tylko wybrane strony" — ma sens wyłącznie przy zakresie, więc checkbox
  // pokazuje się pod polem zakresu, a przy „Wszystkie" nie jest wysyłany.
  const [selectedOnly, setSelectedOnly] = useState(false);

  const models = modelsData?.models ?? [];
  // Domyślnie pierwszy preset (backend zwraca je od najtańszego) — ten sam
  // porządek co picker ARON-a, więc wybór jest przewidywalny.
  const activeModel = models.find((x) => x.value === model) ?? models[0];

  const specOk = allPages || pageSpecValid(spec);
  const canSubmit = !!file && specOk && !!activeModel && !m.create.isPending;

  const submit = () => {
    if (!canSubmit || !file || !activeModel) return;
    m.create.mutate(
      {
        file,
        pages: allPages ? "wszystkie" : spec.trim(),
        model: activeModel.value,
        fast_mode: fast,
        selected_only: !allPages && selectedOnly,
      },
      {
        onSuccess: () => {
          setFile(null);
          setSpec("");
          setAllPages(true);
          // Tryb zostaje jak był — kto anonimizuje seryjnie, nie klika go co plik.
        },
      },
    );
  };

  return (
    <div className="card p-4 space-y-4">
      <div>
        <span className="section-label">Dokument PDF *</span>
        <FileDropZone
          className="mt-1"
          accept="application/pdf,.pdf"
          files={file ? [file] : []}
          onFilesChange={(fs) => setFile(fs[0] ?? null)}
        />
      </div>

      <div>
        <span className="section-label">Strony do anonimizacji</span>
        <div className="mt-1 flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-1.5 text-xs font-mono cursor-pointer select-none">
            <input
              type="radio"
              checked={allPages}
              onChange={() => setAllPages(true)}
            />
            Wszystkie
          </label>
          <label className="flex items-center gap-1.5 text-xs font-mono cursor-pointer select-none">
            <input
              type="radio"
              checked={!allPages}
              onChange={() => setAllPages(false)}
            />
            Wybrane
          </label>
          {/* Wpisanie zakresu samo przełącza na „Wybrane" — pole zostaje aktywne,
              żeby nie trzeba było klikać radio przed pisaniem. */}
          <input
            className="input-base max-w-[220px] text-sm py-1.5"
            placeholder="np. 1-3, 7, 10-12"
            value={spec}
            onChange={(e) => {
              setSpec(e.target.value);
              if (e.target.value) setAllPages(false);
            }}
          />
        </div>
        {!specOk && (
          <p className="mt-1 text-[11px] text-[var(--color-primary)] font-mono">
            Nieczytelny zakres — podaj numery po przecinku lub zakres, np. „1-3, 7".
          </p>
        )}
        {!allPages && (
          <label className="mt-2 flex items-start gap-2 text-xs cursor-pointer select-none">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={selectedOnly}
              onChange={(e) => setSelectedOnly(e.target.checked)}
            />
            <span>
              <span className="font-mono font-medium">Tylko wybrane strony</span>
              <span className="block text-[11px] text-[var(--color-text-muted)]">
                Plik wynikowy będzie zawierał wyłącznie wskazane strony (zanonimizowane) —
                wyciąg z dokumentu. Bez zaznaczenia wynik to cały dokument, w którym zamalowane
                są tylko wskazane strony, a pozostałe zostają w oryginale.
              </span>
            </span>
          </label>
        )}
      </div>

      <div>
        <span className="section-label">Model AI</span>
        {models.length === 0 ? (
          <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
            Brak skonfigurowanych modeli AI na tym środowisku.
          </p>
        ) : (
          <>
            <div className="mt-1 flex flex-wrap gap-1 border border-[var(--color-border)] p-1">
              {models.map((x) => {
                const active = activeModel?.value === x.value;
                return (
                  <button
                    key={x.value}
                    type="button"
                    onClick={() => setModel(x.value)}
                    title={`${x.model} · ${x.detail}${x.hint ? ` — ${x.hint}` : ""}`}
                    className={`inline-flex items-center gap-1 whitespace-nowrap px-2.5 py-1 text-xs font-mono font-medium transition-colors ${
                      active
                        ? "bg-[var(--color-primary)] text-white"
                        : "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
                    }`}
                  >
                    {x.label}
                    <span className={active ? "opacity-70" : "text-[var(--color-text-muted)]"}>
                      {x.cost}
                    </span>
                  </button>
                );
              })}
            </div>
            {/* Endpoint zwraca wyłącznie modele z wizją (anonimizacja szuka też
                podpisów odręcznych i pieczątek na obrazie), więc nie ma tu już
                wariantu „bez wizji". */}
            {activeModel && (
              <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
                {activeModel.model} · {activeModel.detail}
              </p>
            )}
          </>
        )}
      </div>

      <div>
        <label className="flex items-start gap-2 text-xs cursor-pointer select-none">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={fast}
            onChange={(e) => setFast(e.target.checked)}
          />
          <span>
            <span className="font-mono font-medium">Anonimizacja przyspieszona</span>
            <span className="block text-[11px] text-[var(--color-text-muted)]">
              Pomija etapy nadmiarowe: drugą analizę obrazu i kontrolę wizyjną zamalowanej
              strony. Pieczątki, podpisy i logo są wyszukiwane tylko raz, więc plik trzeba
              obejrzeć przed wysyłką. Kontrola formatów (PESEL, NIP, nazwa firmy) i kontrola
              OCR po zamalowaniu zostają. Ok. 3–4× mniej wywołań modelu wizyjnego.
            </span>
          </span>
        </label>
      </div>

      {m.create.isError && (
        <p className="text-xs text-[var(--color-primary)] font-mono">
          {apiErr(m.create.error, "Nie udało się uruchomić anonimizacji.")}
        </p>
      )}

      <div className="flex items-center justify-between gap-3">
        <p className="text-[11px] text-[var(--color-text-muted)]">
          Model wyłącznie <b>wskazuje</b> dane osobowe — plik zamalowuje i rasteryzuje
          serwer. Wynik to PDF, w którym każda strona jest obrazem: zasłoniętego tekstu
          nie da się zaznaczyć ani odzyskać.
        </p>
        <Button size="sm" disabled={!canSubmit} loading={m.create.isPending} onClick={submit}>
          Anonimizuj
        </Button>
      </div>
    </div>
  );
}

function AnonymizationRow({ row, showUser }: { row: DocAnonymization; showUser: boolean }) {
  const m = useDocAnonymizationMutations();
  const [open, setOpen] = useState(false);
  const meta = ANON_STATUS[row.status];
  const running = row.status === "pending" || row.status === "running";
  // Mianownik to WYBRANE strony, nie liczba stron dokumentu: przy „1-3, 7"
  // przebieg ma do zrobienia cztery strony, nie całe sto.
  const totalPages = row.pages.length || row.page_count;
  // Plik CZĘŚCIOWY: przebieg przerwany (błąd modelu, anulowanie, limit czasu)
  // oddaje gotowe strony zamalowane, a resztę w oryginale. Status wiersza zostaje
  // „Błąd"/„Anulowano" — sam odnośnik nie może więc wyglądać jak pełny wynik.
  const partial = !!row.result_file?.url && row.status !== "success";

  return (
    <>
      <tr className="border-t border-[var(--color-border-muted)] hover:bg-[var(--color-bg-elevated)] transition-colors align-top">
        <td className="px-3 py-2.5">
          <button
            className="text-left font-medium text-[var(--color-text-primary)] hover:text-[var(--color-primary)]"
            onClick={() => setOpen((o) => !o)}
          >
            {row.original_filename || "—"}
          </button>
          <div className="text-[11px] font-mono text-[var(--color-text-muted)]">
            {fmtDateTime(row.created_at)}
          </div>
        </td>
        {showUser && (
          <td className="px-3 py-2.5 whitespace-nowrap text-[11px]">
            {row.user_name}
            {row.is_mine && <span className="text-[var(--color-text-muted)]"> (Ty)</span>}
          </td>
        )}
        <td className="px-3 py-2.5 whitespace-nowrap font-mono text-[11px]">
          {row.pages_spec} <span className="text-[var(--color-text-muted)]">z {row.page_count}</span>
          {row.selected_only && (
            <span
              className="ml-1.5 text-[var(--color-text-muted)]"
              title="Plik wynikowy zawiera wyłącznie wybrane strony (wyciąg z dokumentu)"
            >
              · tylko wybrane
            </span>
          )}
        </td>
        <td className="px-3 py-2.5 whitespace-nowrap font-mono text-[11px]">
          {row.model_label || row.model_id}
          {row.fast_mode && (
            <span
              className="ml-1.5 text-[var(--color-text-muted)]"
              title="Anonimizacja przyspieszona: bez drugiej analizy obrazu i kontroli wizyjnej"
            >
              · przyspieszona
            </span>
          )}
        </td>
        <td className="px-3 py-2.5">
          <Badge color={meta.color}>{meta.label}</Badge>
          {running && <Spinner className="ml-2 inline-block" size="sm" />}
          {/* Postęp: przy „W trakcie" pokazuje, że coś się dzieje (przebieg na
              kilkudziesięciu stronach trwa kilkadziesiąt minut), a przy błędzie —
              na której stronie przebieg padł. Przy „Gotowe" byłoby zawsze N/N. */}
          {row.status !== "success" && totalPages > 0 && (
            <div className="mt-1 font-mono text-[11px] text-[var(--color-text-muted)]">
              {row.processed_pages}/{totalPages} stron
            </div>
          )}
          {row.status === "failure" && row.error && (
            <div className="mt-1 max-w-xs text-[11px] text-[var(--color-primary)]">{row.error}</div>
          )}
          {/* Anulowanie ma swój własny, spokojny opis — ten sam tekst na czerwono
              wyglądałby jak usterka do zgłoszenia. */}
          {row.status === "cancelled" && row.error && (
            <div className="mt-1 max-w-xs text-[11px] text-[var(--color-text-muted)]">
              {row.error}
            </div>
          )}
          {partial && (
            <div className="mt-1 max-w-xs text-[11px] text-amber-700">
              Plik częściowy: zanonimizowano {row.processed_pages}/{totalPages} stron, pozostałe są
              w oryginale — lista w szczegółach.
            </div>
          )}
        </td>
        <td className="px-3 py-2.5 whitespace-nowrap font-mono text-[11px]">
          {row.result_file ? row.redaction_count : "—"}
        </td>
        {/* Koszt i czas przebiegu — koszt zapisuje się dopiero na koniec (także
            przy błędzie i anulowaniu: zużyte tokeny trzeba pokazać), a czas
            trwającego przebiegu liczymy do teraz, bo lista i tak odpytuje co 3 s. */}
        <td className="px-3 py-2.5 whitespace-nowrap text-right font-mono text-[11px]">
          {running ? (
            <span className="text-[var(--color-text-muted)]">liczy się…</span>
          ) : (
            fmtAnonCost(row.cost_usd)
          )}
        </td>
        <td
          className="px-3 py-2.5 whitespace-nowrap text-right font-mono text-[11px]"
          title={row.started_at ? `Start: ${fmtDateTime(row.started_at)}` : undefined}
        >
          {fmtRunTime(row.started_at, row.finished_at)}
        </td>
        <td className="px-3 py-2.5">
          <div className="flex justify-end items-center gap-1.5">
            {row.result_file?.url && (
              <ManagedFileDownload
                file={row.result_file}
                className={`inline-flex items-center gap-1 text-[11px] font-mono hover:text-[var(--color-primary)] ${
                  partial ? "text-amber-700" : "text-[var(--color-text-muted)]"
                }`}
                title={
                  partial
                    ? "Pobierz CZĘŚCIOWO zanonimizowany PDF — część stron jest w oryginale"
                    : "Pobierz zanonimizowany PDF"
                }
              >
                <Download size={13} /> {partial ? "Wynik częściowy" : "Wynik"}
              </ManagedFileDownload>
            )}
            {row.source_file?.url && (
              <ManagedFileDownload
                file={row.source_file}
                className="inline-flex items-center gap-1 text-[11px] font-mono text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                title="Pobierz plik źródłowy (nieanonimizowany)"
              >
                <Download size={13} /> Źródło
              </ManagedFileDownload>
            )}
            {/* „Anuluj" tylko na WŁASNYM, trwającym przebiegu — dokładnie jak na
                liście zadań automatycznych, tylko bez chodzenia tam po id zadania.
                Przebieg na kilkudziesięciu stronach liczy się kilkadziesiąt minut i
                płaci za tokeny, więc pomyłka („nie ten plik", „nie te strony") ma się
                dać zatrzymać od razu. */}
            {m.cancel.isError && (
              <span className="max-w-[180px] text-[11px] text-[var(--color-primary)]">
                {apiErr(m.cancel.error, "Nie udało się anulować.")}
              </span>
            )}
            {row.is_mine && running && (
              <Button
                size="sm"
                variant="ghost"
                loading={m.cancel.isPending}
                onClick={() =>
                  window.confirm(
                    `Przerwać anonimizację „${row.original_filename}"? Gotowe strony trafią do pliku częściowego, pozostałe zostaną w oryginale.`,
                  ) && m.cancel.mutate(row.id)
                }
                title="Zatrzymaj zadanie w tle"
              >
                <Ban size={13} className="mr-1" /> Anuluj
              </Button>
            )}
            {/* Cudzej historii nie kasujemy — backend i tak odpowie 403. */}
            {row.is_mine && (
              <Button
                size="sm"
                variant="ghost"
                disabled={running}
                onClick={() =>
                  window.confirm(`Usunąć wpis „${row.original_filename}" wraz z plikami?`) &&
                  m.remove.mutate(row.id)
                }
              >
                Usuń
              </Button>
            )}
          </div>
        </td>
      </tr>
      {open && (
        <tr className="border-t border-[var(--color-border-muted)] bg-[var(--color-bg-base)]">
          <td colSpan={showUser ? 9 : 8} className="px-3 py-2.5">
            {/* Rozmiar wyniku obok źródła: strony wynikowe są rastrem, więc
                serwer dociska je do rozmiaru oryginału — a człowiek ma widzieć,
                co z tego wyszło, zanim wyśle plik dalej. */}
            {row.result_file?.size_bytes != null && (
              <div className="mb-1.5 text-[11px] font-mono text-[var(--color-text-secondary)]">
                Plik wynikowy: {fmtBytes(row.result_file.size_bytes)}
                {row.source_file?.size_bytes != null && (
                  <span className="text-[var(--color-text-muted)]">
                    {" "}
                    · źródło: {fmtBytes(row.source_file.size_bytes)}
                  </span>
                )}
              </div>
            )}
            {row.findings.length === 0 && row.warnings.length === 0 ? (
              <span className="section-label">Brak szczegółów — zadanie jeszcze nie przetworzyło stron.</span>
            ) : (
              <div className="space-y-1.5">
                {row.findings.map((f) => (
                  <div key={f.page} className="text-[11px] font-mono text-[var(--color-text-secondary)]">
                    Strona {f.page} ·{" "}
                    {f.mode === "vision"
                      ? "skan (analiza obrazu)"
                      : f.mode === "text+vision"
                        ? "warstwa tekstowa + obraz (podpisy, pieczątki)"
                        : "warstwa tekstowa"}{" "}
                    · zasłonięto {f.redactions}
                    {/* Rozbicie na warstwy: „bez AI" to formaty z sumą kontrolną
                        (PESEL, NIP, rachunek…), „AI" to wszystko, co model dołożył ponad
                        — łącznie z ramkami z przebiegu graficznego. */}
                    {f.mode.startsWith("text") && (
                      <span className="text-[var(--color-text-muted)]">
                        {" "}
                        · bez AI: {f.auto_hits} · AI: {f.ai_extra_hits}
                      </span>
                    )}
                    {Object.keys(f.categories).length > 0 && (
                      <span className="text-[var(--color-text-muted)]">
                        {" "}
                        ({Object.entries(f.categories)
                          .map(([k, v]) => `${k}: ${v}`)
                          .join(", ")})
                      </span>
                    )}
                  </div>
                ))}
                {row.warnings.map((w) => (
                  <div key={w} className="text-[11px] text-amber-700">
                    ⚠ {w}
                  </div>
                ))}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function AnonymizationTab() {
  // Historia jest prywatna; deweloper może ją rozwinąć na cudze wykonania
  // (diagnostyka zgłoszeń) — poza wykonaniami prezesa, których backend nie wyda.
  const isDeveloper = useAuthStore((s) => !!s.user?.is_developer);
  const [showAll, setShowAll] = useState(false);
  const all = isDeveloper && showAll;
  const { data, isLoading } = useDocAnonymizations(all);
  const rows = data ?? [];

  return (
    <div className="space-y-4">
      <AnonymizationForm />

      <div>
        <div className="mb-2 flex items-center gap-3">
          <h2 className="section-label">
            {all ? "Historia — wszystkie anonimizacje" : "Historia — Twoje anonimizacje"}
          </h2>
          {isDeveloper && (
            <label className="flex items-center gap-1.5 text-xs font-mono cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showAll}
                onChange={(e) => setShowAll(e.target.checked)}
              />
              Pokaż wykonania wszystkich użytkowników
            </label>
          )}
        </div>
        {isLoading ? (
          <div className="py-10 flex justify-center">
            <Spinner />
          </div>
        ) : (
          <TableShell>
            <thead>
              <tr>
                <Th>Dokument</Th>
                {all && <Th>Użytkownik</Th>}
                <Th>Strony</Th>
                <Th>Model</Th>
                <Th>Status</Th>
                <Th>Zasłonięte</Th>
                <Th className="text-right">Koszt AI</Th>
                <Th className="text-right">Czas</Th>
                <Th className="text-right">Akcje</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <AnonymizationRow key={r.id} row={r} showUser={all} />
              ))}
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={all ? 9 : 8}
                    className="px-3 py-10 text-center section-label tracking-wide"
                  >
                    Brak wykonań. Prześlij PDF, wskaż strony i wybierz model AI.
                  </td>
                </tr>
              )}
            </tbody>
          </TableShell>
        )}
      </div>
    </div>
  );
}

type DocGenTab = "templates" | "formatki" | "anonymization";
const DOC_GEN_TABS: DocGenTab[] = ["templates", "formatki", "anonymization"];

export function DocGenerationPage() {
  // Zakładka żyje w URL (?tab=formatki), żeby dało się linkować prosto do
  // sekcji — np. „Zarządzaj →" z kreatora formatek prowadzi tu, nie na osobną stronę.
  const [params, setParams] = useSearchParams();
  const raw = params.get("tab");
  const tab: DocGenTab = DOC_GEN_TABS.includes(raw as DocGenTab) ? (raw as DocGenTab) : "templates";
  const setTab = (k: DocGenTab) =>
    setParams(k === "templates" ? {} : { tab: k }, { replace: true });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Generacja dokumentów"
        subtitle="Szablony ODF i formatek DXF z polami #zmienna# wypełnianymi danymi z systemu · DLC_01"
      />

      <Tabs
        tabs={[
          { key: "templates", label: "Szablony dokumentów (ODF)" },
          { key: "formatki", label: "Szablony formatek (DXF)" },
          { key: "anonymization", label: "Anonimizacja" },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === "templates" && <TemplatesTab />}
      {tab === "formatki" && <FormatkaTemplatesSection />}
      {tab === "anonymization" && <AnonymizationTab />}
    </div>
  );
}
